// Video upload field — rectangular preview area with video player when a file is selected
// Uses accept/maxFiles from field config
import { useRef, useState, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatAcceptList, getStoredAssetUrl, isFileAccepted } from '@startus/lib';
import { FieldError, FieldWrapper } from '@startus/ui';
import { useFormMode } from '../../hooks/useFormMode';
import type { BaseFieldProps } from '../../types/fields';

export function VideoField({ name, label, helpText, accept, maxFiles: _maxFiles }: BaseFieldProps) {
  const { t } = useTranslation('common');
  const { isReadOnly } = useFormMode();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  // Picked file rejected by `accept` — kept out of the form, explained under the field.
  const [typeError, setTypeError] = useState<string | null>(null);
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const value = watch(name);
  const error = errors[name]?.message as string | undefined;

  // Accept string for <input>: [".mp4"] → ".mp4", fallback to video/*
  const acceptAttr = accept?.join(',') ?? 'video/*';

  // Derive preview URL and filename from File object or backend string URL
  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      setFileName(null);
      return;
    }
    if (value instanceof File) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      setFileName(value.name);
      return () => URL.revokeObjectURL(url);
    }
    // BE stores a JSON URL list; legacy non-URL text is treated as "no video".
    const url = getStoredAssetUrl(value);
    setPreviewUrl(url);
    setFileName(url ? (url.split('/').pop() ?? url) : null);
  }, [value]);

  if (isReadOnly) {
    return (
      <FieldWrapper>
        {previewUrl ? (
          <video
            src={previewUrl}
            controls
            className="w-full rounded-lg bg-base-300 max-h-64"
            aria-label={label}
          />
        ) : (
          <div className="w-full h-32 rounded-lg bg-base-300 flex items-center justify-center text-base-content/40 text-sm">
            —
          </div>
        )}
        {helpText && <p className="text-sm text-base-content/60 mt-1">{helpText}</p>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur } }) => (
          <div className="space-y-2">
            {/* Video preview or placeholder */}
            {previewUrl ? (
              <video
                src={previewUrl}
                controls
                className="w-full rounded-lg bg-base-300 max-h-64"
                aria-label={label}
              />
            ) : (
              <div className="w-full h-32 rounded-lg bg-base-300 flex items-center justify-center text-base-content/40 text-sm">
                {t('formFields.noVideo')}
              </div>
            )}

            {/* Filename + controls row */}
            <div className="flex items-center gap-3">
              {fileName && (
                <span className="text-sm text-base-content/70 truncate flex-1">{fileName}</span>
              )}
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="btn btn-sm btn-primary shrink-0"
              >
                {previewUrl ? t('formFields.replace') : t('formFields.uploadVideo')}
              </button>
              {previewUrl && (
                <button
                  type="button"
                  onClick={() => onChange(null)}
                  className="btn btn-sm btn-ghost shrink-0"
                >
                  {t('formFields.removeVideo')}
                </button>
              )}
            </div>

            <input
              type="file"
              accept={acceptAttr}
              ref={inputRef}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                e.target.value = '';
                if (file && !isFileAccepted(file, accept)) {
                  setTypeError(
                    t('formFields.invalidFileType', { formats: formatAcceptList(accept ?? []) })
                  );
                  return;
                }
                setTypeError(null);
                onChange(file);
              }}
              onBlur={onBlur}
            />
          </div>
        )}
      />
      {helpText && <p className="text-sm text-base-content/60 mt-1">{helpText}</p>}
      {typeError ? (
        <p role="alert" className="label-text-alt text-error py-1">
          {typeError}
        </p>
      ) : (
        <FieldError message={error} />
      )}
    </FieldWrapper>
  );
}
